import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, firstValueFrom, Observable, of, Subject} from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { AssistantState, AssistantStep, Usage, ClientDiscussion, ClientThreadSummary, PromptAttachment, PromptSelection, CorpusToggle } from './kng-model.assistant';
import { attachmentsToContextItems } from './kng-prompt-context';
import {
  AgentRun,
  KngDiscussionStreamService,
} from './kng-discussion-stream.service';

export { cleanAssistantConsoleText, isDiscussionStateId, parseSteps } from './kng-discussion-stream.service';

/**
 * Contenu sélectionné dans un éditeur (Tiptap rules-editor) que l'utilisateur
 * souhaite passer en contexte à Edgar lors de la prochaine requête.
 *
 * - `text` : texte brut sélectionné (servira au prompt + à l'aperçu du tag).
 * - `source` : slug/titre court du document source (affiché dans le tag).
 * - `from` / `to` : positions ProseMirror (utile pour réinjecter un patch).
 */
export interface SelectedContent {
  text: string;
  source?: string;
  from?: number;
  to?: number;
}

@Injectable({
  providedIn: 'root'
})
export class KngAssistantAiService {
  private defaultUsage: Usage = {prompt: 0, completion: 0, cost: 0, total: 0};
  private defaultState: AssistantState = { status: 'init', usage: this.defaultUsage};

  private discussionSubject = new BehaviorSubject<ClientDiscussion>({
    id: null,
    messages: [],
    threads: [],
    discussions: [],
    usage: this.defaultUsage,
    createdAt: new Date(),
    updatedAt: new Date()
  });

  private stateSubject = new BehaviorSubject<AssistantState>(this.defaultState);
  private transcriptionsSubject = new BehaviorSubject<string>('');
  private memoriesSubject = new BehaviorSubject<any[]>([]);  // ✅ NEW: Memories state

  // ✅ NEW: Event system for prompt cross-component communication
  private promptEventSubject = new Subject<string>();

  //
  // Legacy fallback for raw context injection.
  // Prefer PromptAttachment[] for new integrations.
  private promptContextSubject = new BehaviorSubject<string | null>(null);
  private promptAttachmentsSubject = new BehaviorSubject<PromptAttachment[]>([]);
  private promptSelectionSubject = new BehaviorSubject<PromptSelection | null>(null);
  private selectedContentSubject = new BehaviorSubject<SelectedContent | null>(null);
  private dossierCloserSubject = new BehaviorSubject<{ class: string; title: string } | null>(null);
  /** Incrémente à chaque history/mint : un GET plus ancien n'applique plus la vue. */
  private viewEpoch = 0;

  // ✅ StateGraph-only observables
  discussion$ = this.discussionSubject.asObservable();
  get runningIds$() {
    return this.$streams.runningIds$;
  }
  state$ = this.stateSubject.asObservable();
  transcriptions$ = this.transcriptionsSubject.asObservable();
  memories$ = this.memoriesSubject.asObservable();  // ✅ NEW: Observable for memories
  promptEvt$ = this.promptEventSubject.asObservable();
  promptContext$ = this.promptContextSubject.asObservable();
  promptAttachments$ = this.promptAttachmentsSubject.asObservable();
  promptSelection$ = this.promptSelectionSubject.asObservable();
  selectedContent$ = this.selectedContentSubject.asObservable();
  dossierCloser$ = this.dossierCloserSubject.asObservable();

  //
  // Ordre: top candidats (fréquence/impact) en premier
  //
  questions = {
    'Contentieux': [
      {
        "question": "Donne-moi la situation locataire (contentieux) pour la référence 2015.042 021.14.",
        "note": "Situation locataire / soldes depuis refLocataire"
      },
      {
        "question": "Constitue le dossier contentieux du locataire 2015.042 021.14",
        "note": "Profil + soldes + documents liés"
      },
      {
        "question": "Peux-tu me donner les honoraires travaux validés pour la relocation de 1459.044 001.22 ?",
        "note": "resolveValidationNouveauLoyer par référence locataire"
      },
    ],
    'Procédures': [
      {
        "question": "Comment traiter une résiliation de bail pour un locataire ?",
        "note": "Procédure contentieux via lookupKnowledge"
      },
      {
        "question": "Quelle est la procédure pour les 4ème rappels et plus (analyses contentieux) ?",
        "note": "Procédure analyses contentieux"
      },
      {
        "question": "Comment traiter les poursuites pour un locataire sorti ?",
        "note": "Procédure poursuites locataires sortis"
      },
      {
        "question": "Comment gérer un arrangement de paiement avec un locataire ?",
        "note": "Procédure arrangements de paiement"
      },
      {
        "question": "Que faire d'un recommandé non réclamé ?",
        "note": "Procédure recommandés non réclamés"
      },
      {
        "question": "Comment traiter les loyers irrécouvrables et gratuités de loyer ?",
        "note": "Procédure loyers irrécouvrables"
      },
    ],
    'Honoraires': [
      {
        "question": "Quelle est la procédure pour traiter une note d'honoraire (NH) ?",
        "note": "Procédure NH via lookupKnowledge"
      },
      {
        "question": "Comment sont traitées les notes d'honoraires aux propriétaires ?",
        "note": "Procédure NH propriétaires"
      },
    ],
  }

  sgc = [
    {
      category: "S1",
      question: "Obtiens la situation locataire pour la référence locataire 2015.042 021.14.",
      note: "Situation locataire / contentieux depuis refLocataire"
    }
  ];

  constructor(
    private $http:HttpClient,
    private $streams: KngDiscussionStreamService,
  ) {
    this.$streams.updates$.subscribe(run => this.applyRun(run));
  }

  // ✅ StateGraph-only getters
  get discussion(): ClientDiscussion {
    return this.discussionSubject.value;
  }

  get discussionMessages(): Array<{id: string, role: 'user' | 'assistant', content: string, timestamp: Date}> {
    return this.discussion.messages;
  }

  get state(): AssistantState {
    return this.stateSubject.value;
  }

  get queries() {
    return this.questions;
  }

  private discussionHeaders() {
    return {
      'Content-Type': 'application/json',
      'ngsw-bypass': 'true',
      'Cache-Control': 'no-cache',
    };
  }

  private applyClientView(res: ClientDiscussion, agent?: string) {
    const current = this.discussionSubject.value;
    const discussions = this.mergeDiscussions(res, current);
    const run = this.$streams.get(res.id);
    const live = run?.status === 'running';
    const rawMessages = (live ? run.messages : res.messages) || [];
    const messages = rawMessages.map((msg: any) => this.stripMemories(msg));
    this.discussionSubject.next({
      ...current,
      ...res,
      id: res.id ?? null,
      messages,
      threads: discussions,
      discussions,
      usage: res.usage || this.defaultUsage,
      closer: res.closer,
    });
    this.dossierCloserSubject.next(res.closer || null);
    const nextStatus = live
      ? 'running'
      : (this.state.status === 'end' || this.state.status === 'running')
        ? 'init'
        : this.state.status;
    this.stateSubject.next({
      ...this.state,
      usage: res.usage || this.defaultUsage,
      agent: res.agent || agent || this.state.agent,
      status: nextStatus,
      ...(live ? {
        content: run.content || undefined,
        steps: run.steps,
      } : {
        content: undefined,
        steps: undefined,
      }),
    });
  }

  private applyRun(run: AgentRun) {
    const current = this.discussionSubject.value;
    this.discussionSubject.next({
      ...current,
      id: run.id,
      messages: run.messages.map(msg => this.stripMemories(msg)),
      closer: run.closer || current.closer,
      updatedAt: new Date(),
    });
    if (run.closer) {
      this.dossierCloserSubject.next(run.closer);
    }
    this.updateState({
      status: run.status,
      content: run.status === 'running' ? run.content || undefined : undefined,
      steps: run.status === 'running' ? run.steps : undefined,
    });
  }

  private stripMemories<T extends { content?: unknown }>(msg: T): T {
    if (typeof msg.content === 'string') {
      return {
        ...msg,
        content: msg.content.replace(/<memories [^>]+[\s\S]*?<\/memories>/g, ''),
      };
    }
    return msg;
  }

  private beginView(): number {
    return ++this.viewEpoch;
  }

  private isCurrentView(epoch: number): boolean {
    return epoch === this.viewEpoch;
  }

  private bootstrapAgent(agentParam: string, epoch: number) {
    return this.$http.get<ClientDiscussion>(`/api/agent/${agentParam}`, {
      withCredentials: true,
      headers: this.discussionHeaders(),
    }).pipe(
      tap((res: ClientDiscussion) => {
        if (!this.isCurrentView(epoch)) {
          return;
        }
        this.applyClientView(res, agentParam);
      })
    );
  }

  private mergeDiscussions(res: Pick<ClientDiscussion, 'discussions' | 'threads'>, current: ClientDiscussion): ClientThreadSummary[] {
    const incoming = res.discussions || res.threads;
    if (incoming?.length) {
      return incoming;
    }
    return current.discussions || current.threads || [];
  }

  private patchDiscussions(res: ClientDiscussion) {
    const current = this.discussionSubject.value;
    const discussions = this.mergeDiscussions(res, current);
    this.discussionSubject.next({
      ...current,
      threads: discussions,
      discussions,
    });
  }

  private recoverMissingDiscussion(error: any, agentParam: string) {
    if (error?.status === 404 && error.error?.error === 'discussion_not_found') {
      this.notify('Discussion introuvable', 'warning', 'exclamation-triangle');
      this.$streams.detach();
      return this.bootstrapAgent(agentParam, this.beginView());
    }
    console.log('🐛 DBG history() error:', error);
    if (!error?.status || error.status === 0 || error.status >= 502) {
      this.applyClientView(this.discussionSubject.value, agentParam);
      return of(this.discussionSubject.value);
    }
    const message = error.error?.message || 'Erreur lors du chargement de l\'historique';
    this.notify(message, 'danger', 'exclamation-triangle');
    this.applyClientView(this.discussionSubject.value, agentParam);
    return of(error);
  }

  updateState(partial: Partial<AssistantState>) {
    this.stateSubject.next({
      ...this.state,
      ...partial
    });
  }

  isBusy(id?: string | null): boolean {
    const key = id === undefined ? this.discussionSubject.value.id : id;
    return this.$streams.alive(key);
  }

  mintDiscussion(agent?: string): Observable<ClientDiscussion> {
    const current = this.discussionSubject.value;
    const epoch = this.beginView();
    const agentParam = agent || current.agent || 'current';
    return this.$http.get<ClientDiscussion>(`/api/agent/${agentParam}?mint=1`, {
      withCredentials: true,
      headers: this.discussionHeaders(),
    }).pipe(
      tap((res: ClientDiscussion) => {
        if (!this.isCurrentView(epoch)) {
          return;
        }
        this.$streams.detach();
        this.applyClientView({ ...res, messages: [] }, agentParam);
        this.updateState({
          status: 'init',
          content: undefined,
          steps: undefined,
        });
      }),
      catchError((error) => {
        if (error?.status === 429) {
          const message = typeof error.error?.message === 'string' && error.error.message
            ? error.error.message
            : 'Maximum de discussions atteint. Supprimez-en une avec la poubelle.';
          this.notify(message, 'warning', 'trash3');
        }
        return of(this.discussionSubject.value);
      }),
    );
  }


  // ✅ NEW: Cross-component prompt communication + chat trigger
  sendPrompt(prompt: string, agent?: string, ragname?: string, rules?: string[], attachments?: PromptAttachment[], selection?: PromptSelection | null) {
    this.promptEventSubject.next(prompt);
    return this.chat(prompt, {runAgent: agent, ragname, rules, attachments, selection});
  }

  /** Dernière sélection corpus du prompt ; `chat()` la joint si l'appelant n'en passe pas. */
  #corpusSelection: CorpusToggle[] | undefined;

  setCorpusSelection(selection?: CorpusToggle[]) {
    this.#corpusSelection = selection?.length ? selection.map(({ name, use }) => ({ name, use })) : undefined;
  }

  /**
   * Définit le contexte actif (ex: emails sélectionnés, document, etc.)
   * - text: contenu brut injecté dans le query sous forme de tag <context>
   * - null: aucun contexte actif
   */
  setPromptContext(text: string | null): void {
    this.promptContextSubject.next(text);
  }

  /** Efface le contexte après envoi du prompt */
  clearPromptContext(): void {
    this.promptContextSubject.next(null);
  }

  setPromptAttachments(attachments: PromptAttachment[]): void {
    this.promptAttachmentsSubject.next(attachments);
  }

  /**
   * Ajoute un attachment à la sélection courante (dédupliqué par type+data).
   */
  addPromptAttachment(attachment: PromptAttachment): void {
    const current = this.promptAttachmentsSubject.value;
    const exists = current.some(a => a.type === attachment.type && a.data === attachment.data);
    if (exists) {
      return;
    }
    this.promptAttachmentsSubject.next([...current, attachment]);
  }

  /**
   * Retire un attachment de la sélection courante.
   */
  removePromptAttachment(type: PromptAttachment['type'], data: string): void {
    const current = this.promptAttachmentsSubject.value;
    this.promptAttachmentsSubject.next(
      current.filter(a => !(a.type === type && a.data === data))
    );
  }

  clearPromptAttachments(): void {
    this.promptAttachmentsSubject.next([]);
  }

  /**
   * Téléverse un PDF comme asset réutilisable pour le chat (ctrlUploadAsset).
   * Aucun document/règle n'est créé : le serveur met le fichier en cache (source='file')
   * et renvoie { slug, href } référençable dans le prompt via un attachment {type:'file'}.
   */
  uploadAsset(file: File): Promise<{ slug: string; href: string; title: string; contentType: string }> {
    const formData = new FormData();
    formData.append('document', file, file.name);

    return firstValueFrom(
      this.$http.post<{ slug: string; href: string; title: string; contentType: string }>(
        '/api/agent/upload/asset',
        formData,
        { withCredentials: true }
      )
    );
  }

  get promptSelection(): PromptSelection | null {
    return this.promptSelectionSubject.value;
  }

  setPromptSelection(selection: PromptSelection | null): void {
    if (!selection || !selection.text?.trim()) {
      this.promptSelectionSubject.next(null);
      return;
    }

    this.promptSelectionSubject.next({
      ...selection,
      text: selection.text.trim()
    });
  }

  clearPromptSelection(): void {
    this.promptSelectionSubject.next(null);
  }

  /**
   * Retourne le contenu sélectionné actif (ou null).
   * Pattern miroir de `KngMemoriesService.selectedMemoryId`.
   */
  get selectedContent(): SelectedContent | null {
    return this.selectedContentSubject.value;
  }

  /**
   * Définit le contenu sélectionné actif (typiquement depuis le popup Tiptap
   * via le bouton "Edgar"). Les composants `kng-prompt` / `kng-prompt-mini`
   * écoutent `selectedContent$` pour afficher un tag visuel removable.
   *
   * Appeler avec `null` pour effacer.
   */
  setSelectedContent(content: SelectedContent | null): void {
    if (!content || !content.text?.trim()) {
      this.selectedContentSubject.next(null);
      return;
    }
    this.selectedContentSubject.next(content);
  }

  /** Efface le contenu sélectionné actif. */
  clearSelectedContent(): void {
    this.selectedContentSubject.next(null);
  }

  emails() {
    const headers = {
      'Content-Type': 'application/json',
      'ngsw-bypass': 'true',
      'Cache-Control': 'no-cache',
    };
    return this.$http.get('/api/emails', {
      withCredentials: true,
      headers
    });
  }

  historyDel(messageId: string, agent?: string) {
    const discussion = this.discussionSubject.value;
    if(!messageId || !discussion.id) {
      return of(discussion);
    }

    const agentParam = agent || 'current';
    return this.$http.delete(`/api/agent/${agentParam}/${discussion.id}/${messageId}`, {
      withCredentials: true,
      headers: {
        'ngsw-bypass': 'true',
        'Cache-Control': 'no-cache',
        'Content-Type': 'application/json',
      }
    }).pipe(
      tap((res: any) => this.applyClientView(res)),
      catchError((error) => {
        const message = error.error?.error === 'message_not_found'
          ? 'Message introuvable'
          : (error.error?.message || 'Erreur lors de la suppression de l\'historique');
        this.notify(message, 'danger', 'exclamation-triangle');
        return of(error);
      })
    );
  }

  memories() {
    return this.$http.get('/api/agent/memories', {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        'ngsw-bypass': 'true',
        'Cache-Control': 'no-cache',
      }
    }).pipe(
      tap((memories: any) => {
        // ✅ Update memories$ observable
        this.memoriesSubject.next(memories);
      })
    );
  }

  memoryDel(id: string): Observable<any> {
    return this.$http.delete(`/api/agent/memories/${id}`, {
      withCredentials: true,
      headers: {
        'ngsw-bypass': 'true',
        'Cache-Control': 'no-cache',
        'Content-Type': 'application/json',
      }
    }).pipe(
      tap(() => {
        // ✅ Update memories$ observable after deletion
        const current = this.memoriesSubject.value;
        this.memoriesSubject.next(current.filter((m: any) => m.id !== id));
        this.notify('Memory deleted successfully', 'success', 'check-circle');
      }),
      catchError((error) => {
        const message = error.error?.message || 'Erreur lors de la suppression de la mémoire';
        this.notify(message, 'danger', 'exclamation-triangle');
        return of(error);
      })
    );
  }

  listDiscussions(agent?: string) {
    const agentParam = agent || 'current';
    return this.$http.get<ClientDiscussion>(`/api/agent/${agentParam}`, {
      withCredentials: true,
      headers: this.discussionHeaders(),
    }).pipe(
      tap((res: ClientDiscussion) => this.patchDiscussions(res))
    );
  }

  clearDiscussion(agent?: string, discussionId?: string | null) {
    const agentParam = agent || 'current';
    const id = discussionId === undefined ? this.discussionSubject.value.id : discussionId;
    if (!id) {
      return this.mintDiscussion(agent);
    }
    return this.$http.post<ClientDiscussion>(`/api/agent/${agentParam}/${id}/clear`, {}, {
      withCredentials: true,
      headers: this.discussionHeaders(),
    }).pipe(
      tap((res: ClientDiscussion) => this.applyClientView({
        ...res,
        id: res.id || id,
        messages: [],
      }, agent)),
      catchError((error) => this.recoverMissingDiscussion(error, agentParam))
    );
  }

  discussionDelete(id: string, agent?: string) {
    if (!id) {
      return of(this.discussionSubject.value);
    }
    const agentParam = agent || 'current';
    return this.$http.delete<ClientDiscussion>(`/api/agent/${agentParam}/${id}`, {
      withCredentials: true,
      headers: this.discussionHeaders(),
    }).pipe(
      tap((res: ClientDiscussion) => {
        const current = this.discussionSubject.value;
        const discussions = this.mergeDiscussions(res, current);
        if (current.id === id) {
          this.$streams.detach();
          this.discussionSubject.next({
            ...current,
            id: null,
            messages: [],
            threads: discussions,
            discussions,
          });
          this.updateState({
            status: 'init',
            content: undefined,
            steps: undefined,
          });
          return;
        }
        this.patchDiscussions(res);
      }),
    );
  }

  history(clear?: boolean, agent?: string, discussionId?: string | null, options?: { pin?: boolean }) {
    const agentParam = agent || 'current';
    if (clear) {
      return this.clearDiscussion(agentParam, discussionId);
    }
    const epoch = this.beginView();
    if (!discussionId) {
      this.$streams.detach();
      return this.bootstrapAgent(agentParam, epoch);
    }
    if (this.$streams.alive(discussionId)) {
      this.$streams.attach(discussionId);
      return of(this.discussionSubject.value);
    }

    this.$streams.detach();
    return this.$http.get<ClientDiscussion>(`/api/agent/${agentParam}/${discussionId}`, {
      withCredentials: true,
      headers: this.discussionHeaders(),
      ...(options?.pin ? { params: { pin: '1' } } : {}),
    }).pipe(
      tap((res: ClientDiscussion) => {
        if (!this.isCurrentView(epoch)) {
          return;
        }
        this.applyClientView(res, agent);
      }),
      catchError((error) => this.recoverMissingDiscussion(error, agentParam))
    );
  }

  chat(prompt: string, options?: {runAgent?: string, ragname?: string, thinking?: boolean, rules?: string[], attachments?: PromptAttachment[], selection?: PromptSelection | null, corpus?: CorpusToggle[]}) {
    const {runAgent, ragname, thinking, rules, attachments, selection, corpus} = options || {};
    prompt = prompt.trim();
    const isShort = ['continue','1','2','3','4','5','6','7','8','9','c','help','ci','cc','cm','cs','ok','edgar','help'].indexOf(prompt) > -1;
    if(!isShort && (prompt.length < 3 || typeof prompt !== 'string'  )) {
      return of('');
    }

    const discussionId = this.discussionSubject.value.id;
    if (this.$streams.alive(discussionId)) {
      return of('');
    }

    const agentParam = runAgent || 'current';
    if (!discussionId) {
      return this.mintDiscussion(agentParam).pipe(
        switchMap((discussion) => discussion.id ? this.chat(prompt, options) : of('')),
      );
    }

    const requestBody: any = {query: prompt, agent: runAgent, thinking };
    if (ragname) {
      requestBody.ragname = ragname;
    }
    if (rules?.length) {
      requestBody.rules = rules;
    }
    if (attachments?.length) {
      requestBody.attachments = attachments;
    }
    if (selection?.text?.trim()) {
      requestBody.selection = selection;
    }
    const corpusSelection = corpus ?? this.#corpusSelection;
    if (corpusSelection?.length) {
      requestBody.corpus = corpusSelection.map(({ name, use }) => ({ name, use }));
    }

    this.addTemporaryUserMessage(prompt, attachments, selection);
    this.createTemporaryAssistantMessage();
    this.updateState({
      status: 'prompt',
      content: prompt,
      agent: runAgent || this.state.agent,
      thinking: thinking || false,
    });
    this.dossierCloserSubject.next(null);

    return this.$streams.start(
      discussionId,
      `/api/agent/${agentParam}/${discussionId}`,
      requestBody,
      this.discussionSubject.value.messages,
    ).pipe(
      tap({
        error: (err) => {
          if (err.status === 409 || err.status === 429) {
            const message = typeof err.error?.message === 'string' && err.error.message
              ? err.error.message
              : (err.status === 429
                ? 'Maximum de discussions atteint. Supprimez-en une avec la poubelle.'
                : 'Discussion occupée');
            this.notify(message, 'warning', err.status === 429 ? 'trash3' : 'exclamation-triangle');
          }
          if (err.status === 404 && err.error?.error === 'discussion_not_found') {
            this.recoverMissingDiscussion(err, agentParam).subscribe();
          }
          this.updateState({
            status: 'error',
            error: err.status === 401 ? 'authentication' : 'server',
            agent: runAgent || this.state.agent,
            content: undefined,
            steps: undefined,
          });
        },
      }),
    );
  }

  /**
   * ✅ Add temporary user message to discussion
   */
  private addTemporaryUserMessage(prompt: string, attachments?: PromptAttachment[], selection?: PromptSelection | null) {
    const currentDiscussion = this.discussionSubject.value;
    const joins = attachmentsToContextItems(attachments || [], selection);
    const tempMessage = {
      id: `temp-user-${Date.now()}`,
      role: 'user' as const,
      content: prompt,
      timestamp: new Date(),
      ...(joins.length ? { joins } : {}),
    };

    // Update discussion with temporary user message
    this.discussionSubject.next({
      ...currentDiscussion,
      messages: [...currentDiscussion.messages, tempMessage],
      updatedAt: new Date()
    });
  }

  /**
   * ✅ Create temporary assistant message at start of streaming
   */
  private createTemporaryAssistantMessage(): string {
    const currentDiscussion = this.discussionSubject.value;
    const messages = [...currentDiscussion.messages];

    let lastUser = -1;
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index].role === 'user') {
        lastUser = index;
        break;
      }
    }
    const message = messages.find((msg, index) =>
      index > lastUser
      && msg.role === 'assistant'
      && msg.id.startsWith('temp-assistant-')
      && !String(msg.content || '').trim()
    );
    if (message) {
      return message.id;
    }

    // Create new temporary assistant message with empty steps
    const tempAssistantMessage = {
      id: `temp-assistant-${Date.now()}`,
      role: 'assistant' as const,
      content: '',
      timestamp: new Date(),
      steps: [] as AssistantStep[]
    };
    messages.push(tempAssistantMessage);

    // Update discussion with new assistant message
    this.discussionSubject.next({
      ...currentDiscussion,
      messages,
      updatedAt: new Date()
    });

    return tempAssistantMessage.id;
  }

  stream(url: string, body: any, options: { raw?: boolean } = {}) {
    return this.$streams.sse(url, body, { raw: options.raw });
  }

  async whisper(state): Promise<string> {
    if ((!state?.blob && !state?.chunk) || state.silent) {
      return '';
    }

    const formData = new FormData();
    formData.append('audio', state.blob);
    formData.append('type', state.type);
    formData.append('file','audio.wav');

    // ✅ Ajouter la transcription précédente comme contexte pour Whisper
    if (state.previousText) {
      formData.append('previousText', state.previousText);
    }

    try {
      const resp = await this.$http.post<{transcription: string}>('/api/transcribe', formData, {
        withCredentials: true
      }).toPromise();

      const transcription = resp.transcription || '';
      this.transcriptionsSubject.next(transcription);
      return transcription;
    } catch (error: any) {
      console.error('❌ Erreur transcription:', error);

      // FIXME  rationalize errors with a function
      //
      // Extraire le message d'erreur du serveur
      // - error.error contient la réponse JSON du serveur
      let errorMessage = 'Erreur lors de la transcription audio';
      let errorIcon = 'exclamation-triangle';

      if (error.status === 413) {
        // Erreur 413 - Fichier trop volumineux
        errorMessage = error.error?.message || 'Le fichier audio est trop volumineux';
        const limit = error.error?.limit || '10MB';
        errorMessage += ` (limite: ${limit})`;
        errorIcon = 'exclamation-octagon';
      } else if (error.status === 400) {
        // Erreur 400 - Format non supporté ou autre erreur
        errorMessage = error.error?.error || error.error?.message || 'Format audio non supporté';
      } else if (error.status === 0) {
        // Erreur réseau
        errorMessage = 'Erreur de connexion au serveur';
      } else {
        // Autres erreurs
        errorMessage = error.error?.message || error.message || errorMessage;
      }

      // Afficher la notification à l'utilisateur
      this.notify(errorMessage, 'danger', errorIcon, 10000);

      // Propager l'erreur pour que les composants puissent réagir
      throw new Error(errorMessage);
    }
  }

  abort() {
    this.updateState({
      status: 'end'
    });
  }

  sendFeedback(evaluation: any, message: any) {
    // your logic with $metric
  }

  notify(message: string, variant = 'primary', icon = 'info-circle', duration = 6000) {
    const escapeHtml = (html: string) => {
      const div = document.createElement('div');
      div.textContent = html;
      return div.innerHTML;
    };

    const alert = Object.assign(document.createElement('sl-alert'), {
      variant,
      closable: true,
      duration,
      innerHTML: `
        <sl-icon name="${icon}" slot="icon"></sl-icon>
        ${escapeHtml(message)}
      `
    });

    // Clean up DOM node after hide animation completes
    alert.addEventListener('sl-after-hide', () => { alert.remove(); });

    document.body.append(alert);
    return alert.toast();
  }
}
